import React, { Fragment, useEffect, useMemo, useState }  from 'react';

import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';

import directorioHelper from '../../Helpers/Directorio';
import { recorrerPosiciones } from '../../Helpers/posiciones';

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

const Directorio = props => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [flag, setFlag] = useState(false);
    const [directorio, setDirectorio] = useState({
        ...editar
    });
    const [nuevo, setNuevo] = useState({});

    useEffect(() => {
        obtenerDirectorio();
    }, []);

    const obtenerDirectorio = async () => {
        let res = await directorioHelper.obtenerDirectorios();
        if (res.success) {
            setData(res.data);
        } else {
            Swal.fire(
                'Error!',
                'No se pudo obtener el directorio, codigo: ' + res.code,
                'error'
            )
        }
    }

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(directorio) !== JSON.stringify(editar);
    // La posición del nuevo se llena sola al abrir el panel; no cuenta como cambio
    const hayCambiosNuevo = Object.entries(nuevo).some(([campo, valor]) => campo !== 'posicion' && valor);

    // leerDato modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNuevo({});
    }

    const panel = usePanelEdicion({ hayCambios: hayCambiosEditar || hayCambiosNuevo, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if ((hayCambiosEditar || hayCambiosNuevo) && !(await confirmarDescartar())) return;
        limpiarBorradores();
        alAbrir?.();
        panel.abrir(nombre);
    }

    const seleccionarPersona = ({ id }) => {
        const indice = data.findIndex(persona => persona._id === id);
        if (indice < 0) return;
        abrirPanel('editar', () => {
            setEditar(indice === index ? editar : data[indice]);
            setIndex(indice);
        });
    }

    const datosPreview = useMemo(() => {
        if (hayCambiosNuevo) return recorrerPosiciones([...data, { ...nuevo, _id: 'nuevo' }], 'posicion', 'nuevo', nuevo.posicion);
        if (editar._id) return recorrerPosiciones(data, 'posicion', editar._id, directorio.posicion);
        return data;
    }, [data, nuevo, hayCambiosNuevo, editar._id, directorio.posicion]);

    // Al agregar hay una posición más: la nueva persona puede ir al final
    const maximaPosicion = esNuevo => data.length + (esNuevo ? 1 : 0);

    const posicionInvalida = (posicion, maximo) => (
        Number.isInteger(posicion) && posicion >= 1 && posicion <= maximo ? null : 'La posición debe ser un número entre 1 y ' + maximo
    );

    const numeroInvalido = numero => (
        /^\d+$/.test(String(numero ?? '').trim()) && Number.isSafeInteger(Number(numero)) ? null : 'El número debe contener solo dígitos'
    );

    const { iframeRef, src } = usePreviewSitio({
        page: 'directorio',
        ruta: '/contacto',
        data: datosPreview,
        onSelect: seleccionarPersona
    });

    useEffect(() => {
        setFlag(false);
        setDirectorio({...editar});
        setTimeout(() => {
            setFlag(true);
        }, 100);
    }, [editar]);


    const valorCampo = e => (
        e.target.name === 'posicion' && e.target.value !== '' ? Number(e.target.value) : e.target.value
    );

    const leerDato = e => {
        let copiaData = [...data];
        
        setDirectorio({
            ...directorio,
            [e.target.name]: valorCampo(e)
        });

        copiaData[index] = { ...copiaData[index], [e.target.name]: valorCampo(e) };
        setData(copiaData);
    }

    const leerNuevo = e => {
        setNuevo({
            ...nuevo,
            [e.target.name]: valorCampo(e)
        });
    }

    const agregar = async e => {
        e.preventDefault();
        const error = numeroInvalido(nuevo.numero) || posicionInvalida(nuevo.posicion, maximaPosicion(true));
        if (error) return Swal.fire('Error!', error, 'error');
        if ((await directorioHelper.agregarDirectorio(nuevo))?.success) recargar();
    }

    const actualizar = async e => {
        e.preventDefault();
        const error = numeroInvalido(directorio.numero) || posicionInvalida(directorio.posicion, maximaPosicion(false));
        if (error) return Swal.fire('Error!', error, 'error');
        if ((await directorioHelper.editarDirectorio(directorio, editar._id))?.success) recargar();
    }

    const eliminar = async () => {
        if ((await directorioHelper.eliminarDirectorio(editar._id))?.success) recargar();
    }


    return (
        <Fragment>
            <SideNav />
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar persona'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNuevo}
                    pie={
                        <button type='submit' form='form_agregar_directorio' className='boton_admin' disabled={!hayCambiosNuevo}>
                            Agregar persona
                        </button>
                    }
                >
                    <form id='form_agregar_directorio' onSubmit={agregar}>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.nombre ?? ''} id="nombre" name='nombre' type="text" className="validate" />
                            <label for="nombre">Nombre</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.puesto ?? ''} id="puesto" name='puesto' type="text" className="validate" />
                            <label for="puesto">Puesto</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.correo ?? ''} id="correo" name='correo' type="text" className="validate" />
                            <label for="correo">Correo</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.numero ?? ''} id="numero" name='numero' type="text" className="validate" />
                            <label for="numero">Número</label>
                        </div>
                        <div className="input-field">
                            <input onChange={leerNuevo} value={nuevo.posicion ?? ''} id="posicion" name='posicion' type="number" min="1" max={maximaPosicion(true)} className="validate" />
                            <label className='active' for="posicion">Posición</label>
                        </div>
                        <p style={estiloAyuda}>1 = primera del directorio (de 1 a {maximaPosicion(true)}). Las demás se recorren.</p>
                    </form>
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar persona'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            <button type='submit' form='form_editar_directorio' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {!flag ? (
                        <Loader />
                    ) : (
                        <form id='form_editar_directorio' onSubmit={actualizar}>
                            <div className="input-field">
                                <input onChange={leerDato} defaultValue={directorio.nombre} id="nombre" name='nombre' type="text" className="validate" />
                                <label className='active' for="nombre">Nombre</label>
                            </div>
                            <div className="input-field">
                                <input onChange={leerDato} defaultValue={directorio.puesto} id="puesto" name='puesto' type="text" className="validate" />
                                <label className='active' for="puesto">Puesto</label>
                            </div>
                            <div className="input-field">
                                <input onChange={leerDato} defaultValue={directorio.correo} id="correo" name='correo' type="text" className="validate" />
                                <label className='active' for="correo">Correo</label>
                            </div>
                            <div className="input-field">
                                <input onChange={leerDato} defaultValue={directorio.numero} id="numero" name='numero' type="text" className="validate" />
                                <label className='active' for="numero">Número</label>
                            </div>
                            <div className="input-field">
                                <input onChange={leerDato} defaultValue={directorio.posicion} id="posicion" name='posicion' type="number" min="1" max={maximaPosicion(false)} className="validate" />
                                <label className='active' for="posicion">Posición</label>
                            </div>
                            <p style={estiloAyuda}>1 = primera del directorio (de 1 a {maximaPosicion(false)}). Al cambiarla, las demás se recorren.</p>
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa de contacto'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Directorio'
                    ayuda='Haz clic en una tarjeta para editarla'
                    acciones={
                        <button type='button' className='boton_admin' onClick={() => abrirPanel('agregar', () => setNuevo({ posicion: data.length + 1 }))}>
                            + Agregar persona
                        </button>
                    }
                />
                
            </div>
        </Fragment>
    );
}


export default Directorio;